import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { forkJoin, from, Observable, of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, take, tap } from 'rxjs/operators';

import { OrderReturnBankComponent } from '../../returns/components/order-return/order-return-bank/order-return-bank.component';
import { OrderReturnBankConfirmationComponent } from '../../returns/components/order-return/order-return-bank-confirmation/order-return-bank-confirmation';
import { ItemOrderService } from '../components/item-order/item-order.service';
import { OrderCancelComponent } from '../modals/order-cancel/order-cancel.component';
import {
    OrderCancelConfirmComponent,
} from '../modals/order-cancel-confirm/order-cancel-confirm.component';
import { OrderEguiComponent } from '../modals/order-egui/order-egui.component';
import { OrderAddressInfo, OrderTypeEnum } from '../models/orders-type.model';
import { OpenSidebarOrderDetail } from '../sections/order-detail/infrastructure/state/order-detail.actions';

import {
    CalculateHasMoreSuccess,
    CalculateNextIndexSuccess,
    CancelOrder, CancelOrderError,
    CancelOrderSuccess,
    CancelSubOrder,
    DisplayDetailOrder, DisplayDetailOrderError,
    DisplayDetailOrderReset,
    DisplayDetailOrderSuccess,
    GetAddressInfoSuccess,
    GetEcoTicketUrlSuccess,
    GetInvoiceReturnSuccess,
    GetInvoiceReturnUrlSuccess,
    GetInvoiceSuccess,
    GetInvoiceUrlSuccess, GetOrderOnlineError,
    GetOrders,
    GetOrdersOnline,
    GetOrdersOnlineSuccess,
    GetOrdersStore,
    GetOrdersStoreSuccess, GetOrderStoreError,
    GetShowEcoTicketSuccess,
    OpenCancelOrderModal,
    OpenCancelOrderModalSuccess,
    OpenConfirmCancelOrderModal,
    OpenEguiInfoModal,
    OpenGiftTicketModal,
    OpenGiftTicketModalSuccess,
    PrintEgui,
    OpenOrderRefundModal,
    OpenOrderRefundModalSuccess,
    RepayOrder,
    RepayOrderSuccess,
    SetOrdersOnline,
    SetOrdersStore,
    OpenOrderRefundConfirmationModal,
    OpenOrderRefundConfirmationModalSuccess,
} from './orders-type.actions';
import {
    getSuborderSelected,
    selectDetailOrder,
    selectDetailOrderId,
    selectItemsSubOrderSelected,
    selectNextIndex,
    selectOrderType,
    selectSubOrderSelected,
} from './orders-type.selectors';

import { AddressService } from '@api/address/address.service';
import { Order } from '@api/order/order.models';
import { OrderService } from '@api/order/order.service';
import { PhysicalStoreService } from '@api/store-locator/physical-store/physical-store.service';
import { getCmsConfigValue, getStore, getStoreAndLanguage } from '@core/infrastructure/state/core.selectors';
import { EnvironmentService } from '@core/services/environment.service';
import { CoreStorageService } from '@core/services/storage/storage.service';
import { TimestampService } from '@core/services/timestamp.service';
import { LazyLoadLegacyService } from '@lazy-load-legacy.service';
import { TransversalEventsService } from '@shared/common/services/transversal-events.service';
import { ShowError } from '@shared/errors/store/errors.actions';
import { NavigationService } from '@shared/services/navigation/navigation.service';
import { selectUserGuest } from '@shared/user/store';
import { DialogMargins, DialogOptions, DialogService, DialogSizes } from '@ui/dialog/dialog.service';
import { OpenModal } from '@ui/dialog/store/modal.actions';

@Injectable()
export class OrdersTypeEffects {
    constructor(
        private readonly actions$: Actions,
        private readonly orderService: OrderService,
        private readonly itemOrderService: ItemOrderService,
        private readonly addressService: AddressService,
        private readonly physicalStoreService: PhysicalStoreService,
        private readonly navigationService: NavigationService,
        private readonly modalService: DialogService,
        private readonly store: Store,
        private readonly timestampSvc: TimestampService,
        // TODO ECOMDUTI-23330 [TechDebt] - Remove lazyLoadLegacyService service when the legacy is unnecessary
        private readonly lazyLoadLegacyService: LazyLoadLegacyService,
        // TODO ECOMDUTI-23330 [TechDebt] - Remove transversalEventsService service when the legacy is unnecessary
        private readonly transversalEventsService: TransversalEventsService,
        // TODO ECOMDUTI-23330 [TechDebt] - Remove storageService service when the legacy is unnecessary
        private readonly storageService: CoreStorageService,
        // TODO ECOMDUTI-23330 [TechDebt] - Remove environmentService service when the legacy is unnecessary
        private readonly environmentService: EnvironmentService,
    ) { }

    public getOrders$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(GetOrders),
            switchMap(() => this.store.select(selectOrderType).pipe(take(1))),
            map((orderType) => {
                if (orderType === OrderTypeEnum.STORE) {
                    return GetOrdersStore();
                }
                return GetOrdersOnline();
            }),
        );
    });

    public getOrdersOnline$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(GetOrdersOnline),
            switchMap(() => this.store.select(selectNextIndex).pipe(take(1))),
            switchMap((nextIndex) => this.orderService.getOrders(nextIndex)),
            map((ordersResponse) => GetOrdersOnlineSuccess({ ordersResponse })),
            // eslint-disable-next-line rxjs/no-unsafe-catch
            catchError((error) => {
                this.store.dispatch(ShowError({
                    error: {
                        title: 'dev.general.error.dialogTitle',
                        subtitle: error.error?.description ?? 'dev.general.error.contact',
                    },
                    modalSize: DialogSizes.SIZE_M,
                    kind: 'general',
                }));
                return of(GetOrderOnlineError());
            }),
        );
    });

    public calculateNextIndexordersOnline$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(GetOrdersOnlineSuccess),
            map(({ ordersResponse }) =>
                CalculateNextIndexSuccess({ nextIndex: ordersResponse.index + ordersResponse.howMany }))
        );
    });

    public calculateHasMoreOrdersOnline$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(GetOrdersOnlineSuccess),
            map(({ ordersResponse }) =>
                CalculateHasMoreSuccess({
                    hasMore: ordersResponse.index + ordersResponse.howMany < ordersResponse.totalResults,
                }))
        );
    });

    public setOrdersOnline$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(GetOrdersOnlineSuccess),
            switchMap(({ ordersResponse }) => {
                let ordersResponseReturned = JSON.parse(JSON.stringify(ordersResponse));

                ordersResponseReturned.orders = ordersResponseReturned.orders
                    .map((order) => this.mapOrderImageLegacy(order));

                return of({ ordersResponse: ordersResponseReturned });
            }),
            map(({ ordersResponse }) => SetOrdersOnline({ orders: ordersResponse.orders }))
        );
    });

    public getOrdersStore$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(GetOrdersStore),
            concatLatestFrom(() => this.store.select(selectNextIndex).pipe(take(1))),
            switchMap(([_, nextIndex]) => this.orderService.getOrdersStore(nextIndex).pipe(
                map((ordersResponse) => GetOrdersStoreSuccess({ ordersResponse })),
                catchError((error) => {
                    this.store.dispatch(ShowError({
                        error: {
                            title: 'dev.general.error.dialogTitle',
                            subtitle: error.error?.description ?? 'dev.general.error.contact',
                        },
                        modalSize: DialogSizes.SIZE_M,
                        kind: 'general',
                    }));
                    return of(GetOrderStoreError());
                }),
            )),
        );
    });

    public calculateNextIndexordersStore$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(GetOrdersStoreSuccess),
            map(({ ordersResponse }) =>
                CalculateNextIndexSuccess({
                    nextIndex: ordersResponse.Offset + ordersResponse.Limit,
                }))
        );
    });

    public calculateHasMoreordersStore$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(GetOrdersStoreSuccess),
            map(({ ordersResponse }) =>
                CalculateHasMoreSuccess({
                    hasMore: ordersResponse.Offset + ordersResponse.Limit < ordersResponse.ListReceiptSummary.length,
                }))
        );
    });

    public setOrdersStore$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(GetOrdersStoreSuccess),
            switchMap(({ ordersResponse }) => {
                if (!ordersResponse.ListReceiptSummary.length) {
                    return of([]);
                }

                const orders$: Array<Observable<{
                    type: string;
                    value: Partial<Order> | unknown;
                }>> = ordersResponse.ListReceiptSummary.map((receipt) => this.orderService.mapReceiptToOrder(receipt).pipe(
                    map((order) => ({
                        type: 'SUCCESS',
                        value: order,
                    })),
                    catchError((err) => of({
                        type: 'ERROR',
                        value: err,
                    })),
                ));
                return forkJoin(orders$).pipe(
                    map((ordersResponseMap) => {
                        return ordersResponseMap
                            .filter((orderResponse) => orderResponse.type === 'SUCCESS')
                            .map(orderResponse => orderResponse.value);
                    })
                );
            }),
            map((orders) => {
                return SetOrdersStore({ orders });
            })
        );
    });

    public displayDetailOrder$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(DisplayDetailOrder),
            concatLatestFrom(() => [
                this.store.select(selectDetailOrderId),
                this.store.select(selectOrderType),
            ]),
            filter(
                ([{ order }, detailDisplayerId, orderType]) =>
                    order.id !== detailDisplayerId && orderType === OrderTypeEnum.ONLINE
            ),
            switchMap(([{ order }]) => {
                return this.orderService.getOrderById(Number(order.id), true)
                    .pipe(
                        map(orderMap => {
                            const updatedOrderMap = { ...orderMap, totalPoints: order.totalPoints };
                            return this.mapOrderImageLegacy(updatedOrderMap);
                        }),
                        switchMap(orderMap => {
                            return this.itemOrderService.hasSubOrders(orderMap).pipe(
                                map(hasSubOrders => ({ orderMap, hasSubOrders }))
                            );
                        }),
                    );
            }),
            mergeMap(({ orderMap, hasSubOrders }) =>
                from([
                    DisplayDetailOrderSuccess({ order: orderMap }),
                    OpenSidebarOrderDetail({ order: orderMap, hasSubOrders }),
                ])),
            // eslint-disable-next-line rxjs/no-unsafe-catch
            catchError((error) => {
                this.store.dispatch(ShowError({
                    error: {
                        title: 'dev.general.error.dialogTitle',
                        subtitle: error.error?.description ?? 'dev.general.error.contact',
                    },
                    modalSize: DialogSizes.SIZE_M,
                    kind: 'general',
                }));
                return of(DisplayDetailOrderError());
            }),
        );
    });

    public displayDetailOrderClose$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(DisplayDetailOrder),
            concatLatestFrom(() => [
                this.store.select(selectDetailOrderId),
            ]),
            filter(([{ order, reload }, detailDisplayerId]) => order.id === detailDisplayerId && !reload),
            map(() => DisplayDetailOrderReset()),
        );
    });

    public displayDetailOrderStore$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(DisplayDetailOrder),
            concatLatestFrom(() => [
                this.store.select(selectDetailOrderId),
                this.store.select(selectOrderType),
            ]),
            filter(
                ([{ order }, detailDisplayerId, orderType]) =>
                    order.id !== detailDisplayerId && orderType === OrderTypeEnum.STORE
            ),
            map(([{ order }]) => DisplayDetailOrderSuccess({ order })),
        );
    });

    public getAddress$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(DisplayDetailOrderSuccess),
            concatLatestFrom(() => [this.store.select(getStore), this.store.select(selectUserGuest)]),
            switchMap(([{ order }, store, isUserGuest]) => {
                if (order.shipping.shippingData.addressId) {
                    return this.addressService.getAddress(order.shipping.shippingData.addressId, isUserGuest).pipe(
                        map((address) => {
                            const city = address.city !== '-' ? address.city : '';
                            const colony = address.colony !== '-' ? address.colony : '';

                            const addressInfo: OrderAddressInfo = {
                                address,
                                addressLines: address.addressLines ? address.addressLines.join(', ') : '',
                                city: city && store.details.enableDistrict ? city.split('#')[1] : city,
                                district: colony && store.details.enableDistrict ? colony.split('#')[1] : colony,
                            };

                            return addressInfo;
                        })
                    );
                }

                return this.store.select(getStoreAndLanguage)
                    .pipe(
                        switchMap(storeAndLanguage =>
                            this.physicalStoreService.getStore(
                                storeAndLanguage.storeId,
                                storeAndLanguage.languageId,
                                order.shipping.shippingData.physicalStoreId
                            )),
                        map(physicalStore => {
                            const city = physicalStore.city !== '-' ? physicalStore.city : '';

                            const addressInfo: OrderAddressInfo = {
                                physicalStore,
                                addressLines: physicalStore.address,
                                city: city && store.details.enableDistrict ? city.split('#')[1] : city,
                            };

                            return addressInfo;
                        })
                    );
            }),
            map((address) => {
                return GetAddressInfoSuccess({ address });
            })
        );
    });

    public getInvoice$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(DisplayDetailOrderSuccess),
            switchMap(({ order }) => this.orderService.getInvoice(Number(order.id), false)),
            map((invoice) => GetInvoiceSuccess({ invoice }))
        );
    });

    public printEgui$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(PrintEgui),
            concatLatestFrom(() => this.store.select(selectDetailOrder)),
            filter(([action, orderDetails]) => !orderDetails.hasBeenRequested || action.eguiModal),
            concatLatestFrom(() => [
                this.store.select(getStoreAndLanguage),
                this.store.select(selectDetailOrderId),
            ]),
            switchMap(([_, storeAndLanguage, orderId]) => this.orderService.printEgui(storeAndLanguage.languageId, orderId)),
            map(() => OpenEguiInfoModal({ hasBeenRequested: false })),
        );
    });

    public noPrintEgui$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(PrintEgui),
            concatLatestFrom(() => this.store.select(selectDetailOrder)),
            filter(([action, orderDetails]) => orderDetails.hasBeenRequested && !action.eguiModal),
            map(() => OpenEguiInfoModal({ hasBeenRequested: true }))
        );
    });

    public getInvoiceReturn$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(DisplayDetailOrderSuccess),
            switchMap(({ order }) => this.orderService.getInvoice(Number(order.id), true)),
            map((invoice) => GetInvoiceReturnSuccess({ invoice }))
        );
    });

    public getInvoiceUrl$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(GetInvoiceSuccess),
            concatLatestFrom(() => [
                this.store.select(selectUserGuest),
                this.store.select(selectDetailOrder),
            ]),
            switchMap(([{ invoice }, isGuest, order]) => this.itemOrderService.getInvoiceUrl(order, invoice, isGuest)),
            map((invoiceUrl) => GetInvoiceUrlSuccess({ invoiceUrl }))
        );
    });

    public getInvoiceReturnUrl$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(GetInvoiceReturnSuccess),
            concatLatestFrom(() => [
                this.store.select(selectUserGuest),
                this.store.select(selectDetailOrder),
            ]),
            switchMap(([{ invoice }, isGuest, order]) => this.itemOrderService.getInvoiceUrl(order, invoice, isGuest)),
            map((invoiceReturnUrl) => GetInvoiceReturnUrlSuccess({ invoiceReturnUrl }))
        );
    });

    public getShowEcoTicket$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(GetInvoiceSuccess),
            concatLatestFrom(() => [
                this.store.select(getStore),
                this.store.select(selectDetailOrder),
            ]),
            map(([{ invoice }, store, order]) => {
                const showEcoTicket = invoice && store.details.isPaperLessEnabled && order.isPaperless;
                return GetShowEcoTicketSuccess({ showEcoTicket });
            }),
        );
    });

    public getEcoTicketUrl$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(GetShowEcoTicketSuccess),
            filter(({ showEcoTicket }) => showEcoTicket),
            concatLatestFrom(() => [
                this.store.select(selectUserGuest),
                this.store.select(selectDetailOrder),
            ]),
            switchMap(([, isGuest, order]) => this.itemOrderService.getEcoTicketUrl(order, isGuest)),
            map((ecoTicketUrl) => GetEcoTicketUrlSuccess({ ecoTicketUrl })),
        );
    });

    public repayOrder$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RepayOrder),
            concatLatestFrom(() => [
                this.store.select(selectUserGuest),
                this.store.select(selectDetailOrder),
            ]),
            filter(([_, __, order]) => order?.repayable),
            tap(([, isGuest, order]) => {
                if (isGuest) {
                    this.navigationService.goToAuthPage();
                } else {
                    this.navigationService.goToRepayPage(order);
                }
            }),
            map(() => RepayOrderSuccess())
        );
    });

    public openCancelOrderModal$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(OpenCancelOrderModal),
            tap(({ hasSubOrders }) => {
                this.modalService.openModalFromComponent(
                    null,
                    OrderCancelComponent,
                    {
                        hasSubOrders,
                    },
                    DialogSizes.SIZE_S,
                    DialogMargins.MARGIN_40,
                    [],
                );
            }),
            map(() => OpenCancelOrderModalSuccess())
        );
    });

    public openEguiInfoModal$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(OpenEguiInfoModal),
            tap(({ hasBeenRequested }) => {
                this.modalService.openModalFromComponent(
                    null,
                    OrderEguiComponent,
                    {
                        hasBeenRequested,
                    },
                    DialogSizes.SIZE_S,
                    DialogMargins.MARGIN_40,
                    [],
                );
            }),
        );
    }, { dispatch: false });

    public cancelOrder$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CancelOrder),
            switchMap(() => this.store.select(selectDetailOrder).pipe(take(1))),
            switchMap((order) => {
                return this.orderService.cancelOrder(order);
            }),
            map(() => {
                return CancelOrderSuccess({ isSubOrder: false });
            }),
            // eslint-disable-next-line rxjs/no-unsafe-catch
            catchError((error) => {
                this.store.dispatch(ShowError({
                    error: {
                        title: 'dev.general.error.dialogTitle',
                        subtitle: error.error?.description ?? 'dev.general.error.contact',
                    },
                    modalSize: DialogSizes.SIZE_M,
                    kind: 'general',
                }));
                return of(CancelOrderError());
            }),
        );
    });

    public cancelSubOrder$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CancelSubOrder),
            concatLatestFrom(() => [
                this.store.select(selectDetailOrder),
                this.store.select(selectSubOrderSelected),
            ]),
            switchMap(([, order, suborderId]) => {
                return this.orderService.cancelSubOrder(order, order.suborders.find(suborder => suborder.id === suborderId));
            }),
            map(() => CancelOrderSuccess({ isSubOrder: true })),
            // eslint-disable-next-line rxjs/no-unsafe-catch
            catchError((error) => {
                this.store.dispatch(ShowError({
                    error: {
                        title: 'dev.general.error.dialogTitle',
                        subtitle: error.error?.description ?? 'dev.general.error.contact',
                    },
                    modalSize: DialogSizes.SIZE_M,
                    kind: 'general',
                }));
                return of(CancelOrderError());
            }),
        );
    });

    public openConfirmCancelOrderModal$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CancelOrderSuccess),
            concatLatestFrom(() => this.store.select(selectDetailOrderId)),
            tap(([{ isSubOrder }, orderId]) => {
                this.modalService.openModalFromComponent(
                    null,
                    OrderCancelConfirmComponent,
                    {
                        orderId,
                        isSubOrder,
                    },
                    DialogSizes.SIZE_S,
                    DialogMargins.MARGIN_40,
                    [DialogOptions.FULL_MOBILE],
                );
                this.store.dispatch(DisplayDetailOrder({ order: { id: orderId }, reload: true }));
            }),
            map(() => OpenConfirmCancelOrderModal())
        );
    });

    public openGiftTicketModal$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(OpenGiftTicketModal),
            concatLatestFrom(() => [
                this.store.select(getSuborderSelected),
                this.store.select(selectItemsSubOrderSelected),
            ]),
            tap(([ _action, suborder, items]) => {
                this.store.dispatch(OpenModal({
                    modalId: 'gift-ticket-download',
                    modalData: {
                        order: suborder,
                        items: items.filter((item) => item.type !== 'VirtualGiftCard' && !item.isCustomized),
                    },
                }));
            }),
            map(() => OpenGiftTicketModalSuccess())
        );
    });

    public openOrderRefundModal$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(OpenOrderRefundModal),
            concatLatestFrom(() => [
                this.store.select(getCmsConfigValue('is_refund_legacy_disabled')).pipe(map((value) => !!value)),
            ]),
            tap(([{ order }, isRefundLegacyDisabled]) => {
                // TODO ECOMDUTI-23639 [TechDebt] - REMOVE THIS BEFORE GO PRODUCTION
                const isPRO = this.environmentService.getEnvironment() === 'PRO';

                // eslint-disable-next-line no-secrets/no-secrets
                const isEcomdutti18181HackEnabled = this.storageService.getValue<boolean>('isEcomdutti18181LegacyEnabled', 'SESSION') as boolean || false;

                const isLegacyForced = !isPRO && !!isEcomdutti18181HackEnabled;

                if (!isRefundLegacyDisabled || isLegacyForced) {
                    this.lazyLoadLegacyService
                        .loadAvailabilityInStoreLegacy()
                        .then(() => {
                            this.transversalEventsService.openOrderRefundForm(order, 'none');
                        })
                        .catch(() => {});
                    return;
                }

                this.modalService.openModalFromComponent(
                    null,
                    OrderReturnBankComponent,
                    {
                        isRefundMode: true,
                        order,
                    },
                    DialogSizes.SIZE_M,
                    DialogMargins.MARGIN_16,
                    [],
                );
            }),
            map(() => OpenOrderRefundModalSuccess())
        );
    });

    public openOrderRefundConfirmationModal$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(OpenOrderRefundConfirmationModal),
            tap(() => {
                this.modalService.openModalFromComponent(
                    null,
                    OrderReturnBankConfirmationComponent,
                    {
                        isRefundMode: true,
                    },
                    DialogSizes.SIZE_M,
                    DialogMargins.MARGIN_16,
                    [],
                );
            }),
            map(() => OpenOrderRefundConfirmationModalSuccess())
        );
    });

    private mapOrderImageLegacy(order: Partial<Order>) {
        const mapOrder = { ...order };
        mapOrder.items = order.items.map((item) => {
            let itemCopy = JSON.parse(JSON.stringify(item));
            if (itemCopy?.colors?.length === 0) {
                let xmediaColor = itemCopy.xmedia?.find((y) => y.colorCode === itemCopy.colorId);
                let timestamp = this.timestampSvc.getTs();
                const imageReference = `${itemCopy.displayReference?.replace('/', '')}${itemCopy.colorId}`;
                if (xmediaColor) {
                    itemCopy.colors.push({
                        id: itemCopy.colorId,
                        name: itemCopy.color,
                        image: {
                            timestamp: timestamp as any,
                            url: `${xmediaColor.path}/${imageReference}`,
                            aux: [],
                            type: [],
                            style: [],
                            availableEstilismo: false,
                        },
                        sizes: [],
                    });
                }
            }
            return itemCopy;
        });
        return mapOrder;
    }
}
